import React, { useRef, useEffect, useMemo, useState } from 'react';
import { useEditorStore } from '../../store/editorStore';
import { useShallow } from 'zustand/react/shallow';

const SCALE = 60;       // px per meter in elevation view
const PAD_LEFT = 60;    // space for Y axis labels
const PAD_TOP = 10;
const PAD_BOTTOM = 24;
const ROW_GAP = 24;     // horizontal gap between rows in elevation

interface Props {
  height?: number;
}

export const ElevationPanel: React.FC<Props> = ({ height = 160 }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [canvasW, setCanvasW] = useState(800);
  const { rows, racks, selectedIds, dcSettings } = useEditorStore(useShallow(state => ({
    rows: state.rows, racks: state.racks, selectedIds: state.selectedIds, dcSettings: state.dcSettings
  })));

  // Responsive canvas width via ResizeObserver
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(entries => {
      const w = entries[0]?.contentRect.width;
      if (w && w > 0) setCanvasW(Math.round(w));
    });
    ro.observe(el);
    setCanvasW(Math.round(el.clientWidth));
    return () => ro.disconnect();
  }, []);

  // Group racks by rowId, sorted by position
  const rowGroups = useMemo(() => {
    const groups: Record<string, { row: typeof rows[string]; rackList: typeof racks[string][] }> = {};
    Object.values(rows).forEach(row => {
      groups[row.id] = { row, rackList: [] };
    });
    Object.values(racks).forEach(rack => {
      if (rack.rowId && groups[rack.rowId]) {
        groups[rack.rowId].rackList.push(rack);
      }
    });
    // Sort racks by rackPosInRow or x position
    Object.values(groups).forEach(g => {
      g.rackList.sort((a, b) => (a.rackPosInRow ?? a.x) - (b.rackPosInRow ?? b.x));
    });
    return Object.values(groups).filter(g => g.rackList.length > 0);
  }, [rows, racks]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const W = canvas.width;
    const H = canvas.height;
    ctx.clearRect(0, 0, W, H);

    // Background
    ctx.fillStyle = '#0d1117';
    ctx.fillRect(0, 0, W, H);

    // Y-axis grid
    const maxHeightM = 3.5; // show up to 3.5m
    const viewH = H - PAD_TOP - PAD_BOTTOM;

    // Grid lines
    for (let m = 0; m <= maxHeightM; m += 0.5) {
      const y = PAD_TOP + viewH - (m / maxHeightM) * viewH;
      ctx.strokeStyle = m % 1 === 0 ? '#1f2937' : '#111827';
      ctx.lineWidth = m % 1 === 0 ? 1 : 0.5;
      ctx.beginPath();
      ctx.moveTo(PAD_LEFT, y);
      ctx.lineTo(W, y);
      ctx.stroke();

      if (m % 1 === 0) {
        ctx.fillStyle = '#4b5563';
        ctx.font = '9px Inter, monospace';
        ctx.textAlign = 'right';
        ctx.fillText(`${m}м`, PAD_LEFT - 4, y + 3);
      }
    }

    // Draw each row group
    let cursorX = PAD_LEFT + 8;
    const trayHeightM = dcSettings.defaultTrayHeightM || 0.3;

    rowGroups.forEach(({ row, rackList }) => {
      const hexColor = row.color || '#3b82f6';
      const rR = parseInt(hexColor.slice(1, 3), 16);
      const rG = parseInt(hexColor.slice(3, 5), 16);
      const rB = parseInt(hexColor.slice(5, 7), 16);

      // Row label
      ctx.fillStyle = hexColor;
      ctx.font = 'bold 9px Inter, monospace';
      ctx.textAlign = 'center';

      const rowStartX = cursorX;

      // Draw each rack
      rackList.forEach(rack => {
        const isSelected = selectedIds.includes(rack.id);
        const rackW = (rack.widthM || 0.6) * SCALE;
        const rackH = (rack.heightM || dcSettings.defaultRackHeightM || 2.0);
        const floorY = PAD_TOP + viewH;
        const rackTopY = floorY - (rackH / maxHeightM) * viewH;
        const rackPixH = (rackH / maxHeightM) * viewH;

        // Rack body
        ctx.fillStyle = isSelected
          ? `rgba(${rR},${rG},${rB},0.5)`
          : `rgba(${rR},${rG},${rB},0.2)`;
        ctx.fillRect(cursorX, rackTopY, rackW - 2, rackPixH);

        // Rack outline
        ctx.strokeStyle = isSelected ? hexColor : `rgba(${rR},${rG},${rB},0.5)`;
        ctx.lineWidth = isSelected ? 1.5 : 0.75;
        ctx.strokeRect(cursorX, rackTopY, rackW - 2, rackPixH);

        // Rack label (vertical)
        ctx.save();
        ctx.translate(cursorX + rackW / 2 - 2, floorY - 4);
        ctx.rotate(-Math.PI / 2);
        ctx.fillStyle = isSelected ? '#f3f4f6' : '#6b7280';
        ctx.font = `${isSelected ? 'bold' : 'normal'} 7px Inter, monospace`;
        ctx.textAlign = 'right';
        ctx.fillText(rack.label, 0, 0);
        ctx.restore();

        cursorX += rackW;
      });

      const rowEndX = cursorX;
      const rowMidX = (rowStartX + rowEndX) / 2;

      // Tray line above the row (at top of racks + trayHeightM)
      const tallestRack = Math.max(...rackList.map(rack2 => rack2.heightM || dcSettings.defaultRackHeightM || 2.0));
      const trayY = PAD_TOP + viewH - ((tallestRack + trayHeightM) / maxHeightM) * viewH;

      ctx.strokeStyle = `rgba(${rR},${rG},${rB},0.8)`;
      ctx.lineWidth = 2;
      ctx.setLineDash([4, 3]);
      ctx.beginPath();
      ctx.moveTo(rowStartX, trayY);
      ctx.lineTo(rowEndX - 2, trayY);
      ctx.stroke();
      ctx.setLineDash([]);

      // Tray height label
      ctx.fillStyle = hexColor;
      ctx.font = '8px Inter, monospace';
      ctx.textAlign = 'center';
      ctx.fillText(`${(tallestRack + trayHeightM).toFixed(1)}м`, rowMidX, trayY - 3);

      // Row name label at bottom
      ctx.fillStyle = hexColor;
      ctx.font = 'bold 9px Inter, monospace';
      ctx.fillText(row.name, rowMidX, H - 6);

      cursorX += ROW_GAP;
    });

    // Floor line
    const floorY = PAD_TOP + viewH;
    ctx.strokeStyle = '#374151';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(PAD_LEFT, floorY);
    ctx.lineTo(W, floorY);
    ctx.stroke();

    // Legend: tray line
    ctx.strokeStyle = '#6b7280';
    ctx.lineWidth = 1.5;
    ctx.setLineDash([4, 3]);
    ctx.beginPath();
    ctx.moveTo(W - 90, PAD_TOP + 10);
    ctx.lineTo(W - 70, PAD_TOP + 10);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.fillStyle = '#6b7280';
    ctx.font = '8px Inter, monospace';
    ctx.textAlign = 'left';
    ctx.fillText('— лоток', W - 68, PAD_TOP + 13);

  }, [rowGroups, selectedIds, dcSettings, rows, canvasW]);

  if (rowGroups.length === 0) {
    return (
      <div style={{
        height, background: 'var(--bg-deep, #060a0f)',
        borderTop: '1px solid var(--border, #1a2744)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: 'var(--text-muted, #6b7280)', fontSize: 11,
      }}>
        Z-вид: нет рядов для отображения по вертикали. Создайте ряды или назначьте стойки в ряд.
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      style={{
        height,
        background: 'var(--bg-deep, #060a0f)',
        borderTop: '1px solid var(--border, #1a2744)',
        position: 'relative',
        flexShrink: 0,
      }}
    >
      <div style={{
        position: 'absolute', top: 6, left: 10,
        fontSize: 10, color: 'var(--text-muted, #6b7280)', fontFamily: 'var(--font-data)', textTransform: 'uppercase', letterSpacing: '0.08em',
        pointerEvents: 'none', zIndex: 1,
      }}>
        Вид сбоку (Z-ось)
      </div>
      <canvas
        ref={canvasRef}
        width={canvasW}
        height={height}
        style={{ display: 'block', width: '100%', height: '100%' }}
      />
    </div>
  );
};
